import React, { useCallback, useEffect, useMemo, useState } from 'react';
import TagManager from 'react-gtm-module';
import { Stack } from 'react-bootstrap';
import {
  BrowserRouter,
  Routes,
  Route,
  useSearchParams,
} from 'react-router-dom';
import { Home } from './pages/Home';
import PageNotFound from './pages/PageNotFound';
import { CustomNavbar } from './components/custom-navbar/CustomNavbar';
// import { TopBar } from './components/topbar/TopBar';
import useWindowSize from '@rooks/use-window-size';
import ScreenSizeContext from './contexts/ScreenSizeContext';
import { Footer } from './components/footer/Footer';
import ItemsContext, { ItemsContextInterface } from './contexts/ItemsContext';
import AuthContext, { AuthContextInterface, AuthUser } from './contexts/AuthContext';
import { fetchMe, setCurrentUid } from './queries';
//import OffCanvasContext, { OffCanvasContextInterface } from './contexts/OffCanvasContext';
import {ContextProvider} from './contexts/Context';
import FiltersContext, {
  FiltersContextInterface,
} from './contexts/FiltersContext';
import { Item } from './components/item-card/ItemCard';
import {
  CPUBrands,
  Retailers,
  GPUBrands,
  SearchKeys,
  SortTypes,
  RAMSizes,
  StorageSizes,
  StorageTypes,
} from './components/sidebar/filterInterfaces';
import ItemsToCompareContext, {
  ItemsToCompareContextInterface,
} from './contexts/ItemsToCompareContext';
import { CompareSelections } from './components/compare-selections/CompareSelections';
import { IFilterStatus, initialFilterStatus, rangeInputConstant } from './components/range-inputs/NewRangeInput';
import {rangeInputParamString } from './utils/functions';
// import { EmailCapture } from './components/Email-Newsletter/EmailCapture';
import { EmailCaptureRating } from './components/Email-Newsletter/EmailCaptureRating';

const tagManagerArgs = {
  gtmId: 'GTM-WC5FMGX8'
};
const EXTRA_SMALL = 576; /* xs */
const LARGE_WIDTH = 992; /* >= LARGE_WIDTH == md in Bootstrap */
const EXTRA_LARGE_WIDTH = 1200;
/* const reload = () => window.location.reload(); */
const Container = () => {
  const { innerWidth } = useWindowSize();

  const [searchParams, setSearchParams] = useSearchParams();
  const isScreenWidthXSmall = useCallback((): boolean => {
    return (innerWidth ?? 0) <= EXTRA_SMALL;
  }, [innerWidth]);
  
  const isScreenWidthLarge = useCallback((): boolean => {
    return (innerWidth ?? 0) >= LARGE_WIDTH;
  }, [innerWidth]);

  const isScreenWidthExtraLarge = useCallback((): boolean => {
    return (innerWidth ?? 0) >= EXTRA_LARGE_WIDTH;
  }, [innerWidth]);

  const ScreenSizeContextValue = useMemo(() => {
    return {
      isScreenWidthLarge: isScreenWidthLarge(),
      isScreenWidthExtraLarge: isScreenWidthExtraLarge(),
      isScreenWidthXSmall: isScreenWidthXSmall(),
      innerWidth: innerWidth ?? 0,
    };
  }, [innerWidth]);
  /* const [showOffCanvas, setShowOffCanvas] = useState<boolean>(false);
  const OffCanvasContextValue = useMemo((): OffCanvasContextInterface => {
    return {
      showOffCanvas,
      setShowOffCanvas,
    };
  }, [showOffCanvas]);*/  
  // Items
  const [items, setItems] = useState<Item[]>([]);
  const ItemsContextValue = useMemo((): ItemsContextInterface => {
    return {
      items,
      setItems,
    };
  }, [items]);

  // Auth
  const [user, setUser] = useState<AuthUser | null>(null);
  const AuthContextValue = useMemo((): AuthContextInterface => {
    return {
      user,
      setUser,
    };
  }, [user]);

  // Restores the logged-in UI (person icon vs login/register links) on page load if a session cookie is still live
  useEffect(() => {
    fetchMe().then((me) => {
      if (me.loggedIn) setUser({ firstName: me.firstName || '', emailId: me.emailId || '', uid: me.uid || 0 });
    });
  }, []);

  // Keeps queries.ts's outgoing requests tagged with the current customer_account id (uid),
  // in sync with whatever AuthContext currently holds
  useEffect(() => {
    setCurrentUid(user ? user.uid : null);
  }, [user]);

  useEffect(() => {
    TagManager.initialize(tagManagerArgs);
  }, []);

  // Items
  const [itemsToCompare, setItemsToCompare] = useState<Item[]>([]);
  const ItemsToCompareContextValue =
    useMemo((): ItemsToCompareContextInterface => {
      return {
        itemsToCompare,
        setItemsToCompare,
      };
    }, [itemsToCompare]);

  const getSortParam = () =>{
      const paramVal = searchParams.get('so');
      switch (paramVal) {
        case SortTypes.PRICE_DESCENDING:
        return(SortTypes.PRICE_DESCENDING)
        case SortTypes.PRICE_ASCENDING:
        return(SortTypes.PRICE_ASCENDING)
        case SortTypes.RAM_DESCENDING:
        return(SortTypes.RAM_DESCENDING)
        case SortTypes.RAM_ASCENDING:
        return(SortTypes.RAM_ASCENDING)
        case SortTypes.CPU_SPEED_DESCENDING:
        return(SortTypes.CPU_SPEED_DESCENDING)
        case SortTypes.CPU_SPEED_ASCENDING:
        return(SortTypes.CPU_SPEED_ASCENDING)
        case SortTypes.STORAGE_SIZE_DESCENDING:
        return(SortTypes.STORAGE_SIZE_DESCENDING)
        case SortTypes.STORAGE_SIZE_ASCENDING:
        return(SortTypes.STORAGE_SIZE_ASCENDING)
        default:
          return(SortTypes.PRICE_ASCENDING)
      }
  }
  // Filters
  // To add a new filter, a new state with its value has to be added here
  // Then the new state has to be bind to a form element (input, select, etc)
  // Name
  const [itemClickedSku, setItemClickedSku] = useState<string>('');

  const [sortBy, setSortBy] = useState<SortTypes>( getSortParam() || SortTypes.PRICE_ASCENDING);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [numberOfPages, setNumberOfPages] = useState<number>(1);
  const [totalRecords, setTotalRecords] = useState<number>(1);

  const [itemName, setItemName] = useState<string>('');

  // Price
  const [minPrice, setMinPrice] = useState<number>(0);
  const [maxPrice, setMaxPrice] = useState<number>(0);

  // RAM Size
   const [minRAM, setMinRAM] = useState<number>(0);
   const [maxRAM, setMaxRAM] = useState<number>(0);

  // CPU Speed
  const [minCPU, setMinCPU] = useState<number>(0);
  const [maxCPU, setMaxCPU] = useState<number>(0);

  // CPU Brands
  const [CPUAMD, setCPUAMD] = useState<boolean>(false);
  const [CPUIntel, setCPUIntel] = useState<boolean>(false);
  const [CPUApple, setCPUApple] = useState<boolean>(false);
  const [CPUMediaTek, setCPUMediaTek] = useState<boolean>(false);

  // Retailer
  const [RetailerWalmart, setRetailerWalmart] = useState<boolean>(false);
  const [RetailerDell, setRetailerDell] = useState<boolean>(false);
  const [RetailerBestBuy, setRetailerBestBuy] = useState<boolean>(false);
  const [RetailerTarget, setRetailerTarget] = useState<boolean>(false);
  
  // GPU Brands
  const [GPUAMD, setGPUAMD] = useState<boolean>(false);
  const [GPUIntel, setGPUIntel] = useState<boolean>(false);
  const [GPUApple, setGPUApple] = useState<boolean>(false);
  const [GPUNvidia, setGPUNvidia] = useState<boolean>(false);
  const [GPUMediaTek, setGPUMediaTek] = useState<boolean>(false);

  // Storage Type
  const [HDD, setHDD] = useState<boolean>(false);
  const [SSD, setSSD] = useState<boolean>(false);
  const [eMMC, setEmmc] = useState<boolean>(false);

  // Storage Size
  const [Any, setAny] = useState<boolean>(true);
  
  const [newSearch, setNewSearch] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [textFilterValues, setTextFilterValues] = useState(initialFilterStatus);

  const getSelectedCPUBrandsList = useCallback((): string => {
    let str = '';
    if (CPUAMD) str += `,${CPUBrands.AMD}`;
    if (CPUIntel) str += `,${CPUBrands.INTEL}`;
    if (CPUApple) str += `,${CPUBrands.APPLE}`;
    if (CPUMediaTek) str += `,${CPUBrands.MEDIATEK}`;

    // Replaces the first commma
    return str.replace(',', '');
  }, [CPUAMD, CPUIntel, CPUApple, CPUMediaTek]);

  const getSelectedRetailersList = useCallback((): string => {
    let str = '';
    if (RetailerWalmart) str += `,${Retailers.Walmart}`;
    if (RetailerDell) str += `,${Retailers.DELL}`;
    if (RetailerBestBuy) str += `,${Retailers.BestBuy}`;
    if (RetailerTarget) str += `,${Retailers.Target}`;

    // Replaces the first commma
    return str.replace(',', '');
  }, [RetailerWalmart, RetailerDell, RetailerBestBuy, RetailerTarget]);
  
  const getSelectedGPUBrandsList = useCallback((): string => {
    let str = '';
    if (GPUAMD) str += `,${GPUBrands.AMD}`;
    if (GPUIntel) str += `,${GPUBrands.INTEL}`;
    if (GPUApple) str += `,${GPUBrands.APPLE}`;
    if (GPUNvidia) str += `,${GPUBrands.NVIDIA}`;
    if (GPUMediaTek) str += `,${GPUBrands.MEDIATEK}`;

    // Replaces the first commma
    return str.replace(',', '');
  }, [GPUAMD, GPUIntel, GPUApple, GPUNvidia, GPUMediaTek]);

  const getSelectedStorageTypesList = useCallback((): string => {
    let str = '';
    if (HDD) str += `,${StorageTypes.HDD}`;
    if (SSD) str += `,${StorageTypes.SSD}`;
    if (eMMC) str += `,${StorageTypes.eMMC}`;

    // Replaces the first commma
    return str.replace(',', '');
  }, [HDD, SSD, eMMC]);

  const clearFilters = async (): Promise<void> => {
    setMinPrice(0);
    setMaxPrice(0);

    // RAM Size
    setMinRAM(0);
    setMaxRAM(0);

    // CPU Speed
    setMinCPU(0);
    setMaxCPU(0);

    // CPU Brands
    setCPUAMD(false);
    setCPUIntel(false);
    setCPUApple(false);
    setCPUMediaTek(false);

    // GPU Brands
    setGPUAMD(false);
    setGPUIntel(false);
    setGPUApple(false);
    setGPUNvidia(false);
    setGPUMediaTek(false);

    //Retailers
    setRetailerWalmart(false);
    setRetailerDell(false);
    setRetailerBestBuy(false);
    setRetailerTarget(false);

    // Storage Type
    setHDD(false);
    setSSD(false);
    setEmmc(false);

    // Storage Size
    setAny(true);
    
    //search string
    setItemName("")
  };

  const inputExchanger = {
    [rangeInputConstant.Price] : {min:minPrice , max:maxPrice},
    [rangeInputConstant.RAMSize] : {min:minRAM , max:maxRAM},
    [rangeInputConstant.CPUSpeed] : {min:minCPU , max:maxCPU},
    [rangeInputConstant.StorageSize] : {min:textFilterValues[rangeInputConstant.StorageSize].values.min , max:textFilterValues[rangeInputConstant.StorageSize].values.max},
  }

  //this function passes the obj with current range filter value for param , use it to avoid toggling in between checkbox value and input value.
  const rangeInputValue = () => {
    const obj = JSON.parse(JSON.stringify(textFilterValues));
    
    for (const key in obj) {
      
      const isInput: boolean = textFilterValues[key].current === "input";
      if (!isInput) {
        obj[key].values.min = inputExchanger[key].min === 0 ? "" : inputExchanger[key].min;
        obj[key].values.max = inputExchanger[key].max === 0 ? "" : inputExchanger[key].max;
      } else {
        obj[key].values.min = textFilterValues[key].values.min;
        obj[key].values.max = textFilterValues[key].values.max;
      }
    }
    
    return obj;
  }

  const setQueryParams = useCallback(async (pageReset = true, forceSearch = false, entryPointId?: string): Promise<void> => {
    const rangeInput = rangeInputValue(); 
    setNewSearch(true);
    const keys: SearchKeys = {
      pName: itemName,
      ret: getSelectedRetailersList(),
      prl: rangeInput[rangeInputConstant.Price].values.min,
      prh: rangeInput[rangeInputConstant.Price].values.max,
      raml: rangeInput[rangeInputConstant.RAMSize].values.min,
      ramh: rangeInput[rangeInputConstant.RAMSize].values.max,
      cpul: rangeInput[rangeInputConstant.CPUSpeed].values.min,
      cpuh: rangeInput[rangeInputConstant.CPUSpeed].values.max,
      cpubr: getSelectedCPUBrandsList(),
      gpubr: getSelectedGPUBrandsList(),
      styp: getSelectedStorageTypesList(),
      so: sortBy,
      //   pno  : page number starts at 1, if the number provided is > last page, last page will be served. page size is set at 25 on the server
      pno: pageReset ? 1 : pageNumber,
      rI:rangeInputParamString(textFilterValues),
      sszl:rangeInput[rangeInputConstant.StorageSize].values.min,
      sszh:rangeInput[rangeInputConstant.StorageSize].values.max,
      ...(forceSearch && { ts: Date.now().toString() }),
      ...(entryPointId && { id: entryPointId }),
    };
    const params = Object.keys(keys)
      .map((key) => `${key}=${keys[key as keyof SearchKeys]}`)
      .join('&');
    // window.history.replaceState({}, '', newurl);
    setSearchParams(params);
  }, [
    getSelectedCPUBrandsList,
    getSelectedGPUBrandsList,
	getSelectedRetailersList,
    getSelectedStorageTypesList,
    itemName,
    minPrice,
    maxPrice,
    minRAM,
    maxRAM,
    minCPU,
    maxCPU,
    sortBy,
    pageNumber,
    textFilterValues  
  ]);

  const SidebarFiltersContextValue = useMemo((): FiltersContextInterface => {
    return {
      itemClickedSku,
      setItemClickedSku,

      clearFilters,
      setQueryParams,

      sortBy,
      setSortBy,

      pageNumber,
      setPageNumber,

      numberOfPages,
      setNumberOfPages,

      totalRecords,
      setTotalRecords,

      itemName,
      setItemName,

      minPrice,
      setMinPrice,
      maxPrice,
      setMaxPrice,

      // RAM Size
      minRAM,
      setMinRAM,
      maxRAM,
      setMaxRAM,

      // CPU Speed
      minCPU,
      setMinCPU,
      maxCPU,
      setMaxCPU,

      // CPU Brands
      CPUAMD,
      setCPUAMD,
      CPUIntel,
      setCPUIntel,
      CPUApple,
      setCPUApple,
      CPUMediaTek,
      setCPUMediaTek,

      // GPU Brands
      GPUAMD,
      setGPUAMD,
      GPUIntel,
      setGPUIntel,
      GPUApple,
      setGPUApple,
      GPUNvidia,
      setGPUNvidia,
      GPUMediaTek,
      setGPUMediaTek,
      //Retailer
      RetailerWalmart,
      setRetailerWalmart,
      RetailerDell,
      setRetailerDell,
      RetailerBestBuy,
      setRetailerBestBuy,
      RetailerTarget,
      setRetailerTarget,

      // Storage Type
      HDD,
      setHDD,
      SSD,
      setSSD,
      eMMC,
      setEmmc,

      // Storage Size
      Any,
      setAny,
      
      newSearch,
      setNewSearch,

      loading,
      setLoading,
      
      textFilterValues, setTextFilterValues
    };
  }, [
    itemClickedSku,
    itemName,
    sortBy,
    pageNumber,
    numberOfPages,
    totalRecords,
    minPrice,
    maxPrice,
    minRAM,
    maxRAM,
    minCPU,
    maxCPU,
    CPUAMD,
    CPUIntel,
    CPUApple,
    CPUMediaTek,
    GPUAMD,
    GPUIntel,
    GPUApple,
    GPUNvidia,
    GPUMediaTek,
	RetailerWalmart,
	RetailerDell,
	RetailerBestBuy,
	RetailerTarget,
    HDD,
    SSD,
    eMMC,
    Any,
    newSearch,
    loading,
    setLoading,
    textFilterValues, setTextFilterValues
  ]);
  
  const isCompare = useCallback((): boolean => {
    const searchParamsString = searchParams.toString();
    return (
      searchParamsString.includes('skus') &&
      searchParamsString.includes('websitecodes')
    );
  }, [searchParams]);

  return (
    <AuthContext.Provider value={AuthContextValue}>
    <ScreenSizeContext.Provider value={ScreenSizeContextValue}>
      <FiltersContext.Provider value={SidebarFiltersContextValue}>
        <ItemsContext.Provider value={ItemsContextValue}>
          <ItemsToCompareContext.Provider value={ItemsToCompareContextValue}>
            <Stack className="App">
              {/* <BrowserRouter> */}
              {/* {ScreenSizeContextValue.isScreenWidthLarge && <TopBar />} */}
              {/* <ContextProvider>  
                <CustomNavbar />
              </ContextProvider> */}
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/index.html" element={<Home />} />
                <Route path="*" element={ <PageNotFound/>} />
              </Routes>
              {/* <Footer /> */}
              {itemsToCompare.length > 0 && !isCompare() && (
                <CompareSelections />
              )}
                {/* <EmailCapture /> */}
                <EmailCaptureRating />
              {/* </BrowserRouter> */}
            </Stack>
          </ItemsToCompareContext.Provider>
        </ItemsContext.Provider>
      </FiltersContext.Provider>
    </ScreenSizeContext.Provider>
    </AuthContext.Provider>
  );
};
const App = () => {
  return (
    <BrowserRouter>
      <Container />
    </BrowserRouter>
  );
};
export default App;
